import React from "react";
import {
  Modal,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  Platform,
} from "react-native";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-native-paper";
import { Picker } from "react-native";
import { useToast } from "react-native-toast-notifications";
import { updateChinaOrderPriority } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";
import { useAppTheme } from "../../../hooks/useAppTheme";


interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

interface FormData {
  priority: string;
}

const TrackerPriorityModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const { loadingPriority } = useSelector((state: any) => state.chinaTracker);

  const toast = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      priority: order?.PriorityOrder || "",
    },
  });

  const onSubmit = async (data: FormData) => {
    const payload: any = {
      key: order.Key,
      orderNumber: order.OrderNumber1,
      priority: data.priority,
    };
    try {
      // Replace with your actual dispatch call
      await dispatch(updateChinaOrderPriority(payload))
        .unwrap()
        .then(() => {
          toast.show("Priority Updated successfully", {
            type: "success",
          });
          onClose(); // Close modal after success
        })
        .catch(() => {
          toast.show("Error updating priority", {
            type: "danger",
          });
        });
    } catch (err) {
      console.error("Priority update failed:", err);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t("updatePriority")}: {order?.OrderNumber1}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.inputLabel}>{t("selectPriority")}</Text>
          {!loadingPriority && (
            <>
              <Controller
                control={control}
                name="priority"
                rules={{ required: "Priority is required" }}
                render={({ field: { onChange, value } }) => (
                  <View
                    style={[
                      styles.pickerWrapper,
                      Platform.OS === "android" && {
                        borderWidth: 1.5,
                        borderColor: themeColors.textSecondary,
                        borderRadius: 10,
                      },
                    ]}
                  >
                    <Picker
                      selectedValue={value}
                      onValueChange={onChange}
                      style={styles.picker}
                    >
                      <Picker.Item label="-- Select Priority --" value="" />
                      <Picker.Item label={t("high")} value="High" />
                      <Picker.Item label={t("medium")} value="Medium" />
                      <Picker.Item label={t("normal")} value="Normal" />
                    </Picker>
                  </View>
                )}
              />
              {errors.priority && (
                <Text style={styles.errorStyle}>{errors.priority.message}</Text>
              )}

              <Button
                mode="contained"
                onPress={handleSubmit(onSubmit)}
                style={styles.submitButton}
              >
                Submit
              </Button>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: colors.surface,
    padding: 25,
    width: 500,
    borderRadius: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    flexShrink: 1,
  },
  closeButton: {
    backgroundColor: "#1E88E5",
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  closeButtonText: {
    color: colors.surface,
    fontSize: 16,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
    fontSize: 14,
  },
  pickerWrapper: {
    marginBottom: 10,
  },
  picker: {
    height: 50,
    width: "100%",
  },
  errorStyle: {
    color: "red",
    fontSize: 13,
    marginBottom: 10,
  },
  submitButton: {
    marginTop: 15,
    backgroundColor: colors.text,
  },
});
}

export default TrackerPriorityModal;
